/**
 * API Client for IAM Service
 * Handles all HTTP requests to the backend.
 * Authentication uses Bearer tokens (stored in sessionStorage).
 */

const TOKEN_KEY = 'iam_access_token';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

export class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || '';
  }

  getToken(): string | null {
    return sessionStorage.getItem(TOKEN_KEY);
  }

  setToken(token: string): void {
    sessionStorage.setItem(TOKEN_KEY, token);
  }

  clearToken(): void {
    sessionStorage.removeItem(TOKEN_KEY);
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // still needed for device-trust cookie
    });

    const data: ApiResponse<T> = await response.json();

    if (!response.ok || !data.success) {
      throw new ApiError(
        data.error?.message || 'Request failed',
        data.error?.code || 'UNKNOWN_ERROR',
        data.error?.details
      );
    }

    return data.data as T;
  }

  // Auth endpoints
  async login(email: string, password: string) {
    const result = await this.request<{
      access_token?: string;
      token_type?: string;
      expires_in?: number;
      user?: {
        user_id: string;
        email: string;
        name: string;
        type: string;
      };
      mfa_required?: boolean;
      must_change_password?: boolean;
      auth_code?: string;
      redirect_uri?: string;
      state?: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (result.access_token) {
      this.setToken(result.access_token);
    }

    return result;
  }

  async verifyMFA(code: string, rememberDevice: boolean = false) {
    const result = await this.request<{
      access_token?: string;
      token_type?: string;
      expires_in?: number;
      user: {
        user_id: string;
        email: string;
        name: string;
        type: string;
      };
      must_change_password?: boolean;
      auth_code?: string;
      redirect_uri?: string;
      state?: string;
    }>('/auth/verify-mfa', {
      method: 'POST',
      body: JSON.stringify({ code, remember_device: rememberDevice }),
    });

    if (result.access_token) {
      this.setToken(result.access_token);
    }

    return result;
  }

  async logout() {
    const result = await this.request<{ message: string }>('/auth/logout', {
      method: 'POST',
    });
    this.clearToken();
    return result;
  }

  async forgotPassword(email: string) {
    return this.request<{ message: string; expires_in_minutes: number }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ 
        token, 
        password: newPassword,
        password_confirm: newPassword 
      }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: newPassword,
      }),
    });
  }

  async getUserInfo() {
    return this.request<{
      user_id: string;
      email: string;
      name: string;
      is_admin: boolean;
      email_verified: boolean;
      mfa_enabled: boolean;
    }>('/oauth/userinfo', {
      method: 'GET',
    });
  }

  async getPasswordRequirements() {
    return this.request<{
      min_length: number;
      max_length: number;
      require_uppercase: boolean;
      require_lowercase: boolean;
      require_number: boolean;
      require_special: boolean;
      special_chars: string;
    }>('/auth/password-requirements', {
      method: 'GET',
    });
  }

  // OAuth endpoints
  async authorize(params: {
    clientId: string;
    redirectUri: string;
    state: string;
    codeChallenge: string;
  }): Promise<{ redirect_url: string; code: string; state: string }> {
    const searchParams = new URLSearchParams({
      client_id: params.clientId,
      redirect_uri: params.redirectUri,
      state: params.state,
      response_type: 'code',
      code_challenge: params.codeChallenge,
      code_challenge_method: 'S3-256',
    });

    return this.request(`/oauth/authorize?${searchParams.toString()}`, {
      method: 'GET',
    });
  }
}

export const apiClient = new ApiClient();

